import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f9f7f4',
  padding: '120px 0',
  position: 'relative',
  // Responsive padding
  [theme.breakpoints.down('xl')]: {
    padding: '100px 0'
  },
  [theme.breakpoints.down('lg')]: {
    padding: '80px 0'
  },
  [theme.breakpoints.down('md')]: {
    padding: '60px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '40px 0'
  },
  '@media (max-width: 480px)': {
    padding: '30px 0'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '140px 0'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#B4A082',
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  fontSize: '0.9rem',
  marginBottom: '24px',
  textAlign: 'center',
  // Responsive font sizes
  [theme.breakpoints.down('lg')]: {
    fontSize: '0.85rem',
    marginBottom: '20px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '0.8rem',
    marginBottom: '18px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    marginBottom: '16px'
  },
  '@media (max-width: 480px)': {
    fontSize: '0.7rem',
    marginBottom: '14px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1rem',
    marginBottom: '28px'
  }
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  color: '#2c2c2c',
  fontWeight: 300,
  lineHeight: 1.2,
  marginBottom: '80px',
  fontSize: '3.5rem',
  letterSpacing: '1px',
  fontFamily: '"Playfair Display", serif',
  textAlign: 'center',
  // Responsive font sizes
  [theme.breakpoints.down('xl')]: {
    fontSize: '3.2rem',
    marginBottom: '70px'
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '2.8rem',
    marginBottom: '60px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '2.3rem',
    marginBottom: '50px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.8rem',
    marginBottom: '40px',
    lineHeight: 1.3
  },
  '@media (max-width: 480px)': {
    fontSize: '1.5rem',
    marginBottom: '30px',
    lineHeight: 1.4
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '4rem',
    marginBottom: '100px'
  }
}));

const ServiceCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '0px',
  overflow: 'hidden',
  height: '350px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  // Responsive heights
  [theme.breakpoints.down('xl')]: {
    height: '340px'
  },
  [theme.breakpoints.down('lg')]: {
    height: '320px'
  },
  [theme.breakpoints.down('md')]: {
    height: '300px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '280px'
  },
  '@media (max-width: 480px)': {
    height: '250px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    height: '400px'
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)'
  },
  '&:hover .service-overlay': {
    transform: 'translateY(0)',
    opacity: 1
  },
  '&:hover .service-title': {
    opacity: 0,
    transform: 'translateY(-20px)'
  }
}));

const ServiceImage = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  }
}));

const ServiceTitle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  zIndex: 2,
  color: 'white',
  transition: 'all 0.3s ease'
}));

const ServiceOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '100%',
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.7) 100%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  padding: '40px 30px',
  zIndex: 3,
  color: 'white',
  transform: 'translateY(100%)',
  opacity: 0,
  transition: 'all 0.4s ease',
}));

const ServiceButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#455b40',
  color: 'white',
  border: '2px solid #6c8c68',
  borderRadius: '50px',
  padding: '12px 30px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.9rem',
  letterSpacing: '0.8px',
  marginTop: '20px',
  minWidth: '180px',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  // Responsive sizing
  [theme.breakpoints.down('lg')]: {
    padding: '10px 26px',
    fontSize: '0.85rem',
    minWidth: '160px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '10px 24px',
    fontSize: '0.85rem',
    minWidth: '150px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 20px',
    fontSize: '0.8rem',
    minWidth: '140px',
    marginTop: '16px'
  },
  '@media (max-width: 480px)': {
    padding: '8px 18px',
    fontSize: '0.75rem',
    minWidth: '130px',
    marginTop: '14px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '14px 36px',
    fontSize: '1rem',
    minWidth: '200px'
  },
  '&:hover': {
    backgroundColor: '#6c8c68',
    color: 'white',
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
  }
}));

const ReadMoreLink = styled(Typography)(({ theme }) => ({
  color: '#455b40',
  fontSize: '0.9rem',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  marginTop: '8px',
  textAlign: 'left',
  // Responsive font sizes
  [theme.breakpoints.down('lg')]: {
    fontSize: '0.85rem'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '0.8rem',
    marginTop: '6px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    marginTop: '5px'
  },
  '@media (max-width: 480px)': {
    fontSize: '0.7rem',
    marginTop: '4px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1rem',
    marginTop: '10px'
  },
  '&:hover': {
    color: '#8a7d70',
    textDecoration: 'underline'
  }
}));

interface Course {
  id: number;
  slug: string;
  title: {
    rendered: string;
  };
  acf: {
    image_url_home: string;
  };
}

const CourseOverview: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await fetch(`https://thomass362.sg-host.com/wp-json/wp/v2/cursos?per_page=100&acf_format=standard&_=${timestamp}`);
        const data = await response.json();
        // Sort by ID in ascending order (lowest first) and take first 3
        const sortedCourses = data.sort((a: Course, b: Course) => a.id - b.id);
        setCourses(sortedCourses.slice(0, 3)); // Ensure max 3 courses
        setLoading(false);
      } catch (error) {
        console.error('Error fetching courses:', error);
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  return (
    <SectionContainer>
      <Box sx={{ 
        maxWidth: { xs: '100%', sm: '100%', md: '1200px', lg: '1300px', xl: '1400px' },
        '@media (min-width: 1920px)': {
          maxWidth: '1600px'
        },
        mx: 'auto', 
        px: { xs: 2, sm: 3, md: 4, lg: 3 }
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 3, sm: 4, md: 5, lg: 6 },
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'center', lg: 'flex-start' }
        }}>
          {/* Text Section */}
          <Box sx={{ 
            width: { xs: '100%', sm: '100%', md: '100%', lg: '500px', xl: '600px' },
            '@media (min-width: 1920px)': {
              width: '700px'
            },
            flexShrink: 0,
            px: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant="h2" 
              sx={{ 
                color: '#000000',
                fontWeight: 300,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem', lg: '3rem', xl: '3.5rem' },
                '@media (min-width: 1920px)': {
                  fontSize: '4rem'
                },
                '@media (max-width: 480px)': {
                  fontSize: '1.5rem'
                },
                lineHeight: { xs: 1.3, sm: 1.25, md: 1.2 },
                mb: { xs: 2, sm: 2.5, md: 3 },
                letterSpacing: '1px',
                fontFamily: '"Lora", serif',
                textAlign: { xs: 'center', lg: 'left' }
              }}
            >
              Aprende a cultivar salud con nuestros cursos
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666',
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.05rem', lg: '1.1rem' },
                '@media (min-width: 1920px)': {
                  fontSize: '1.2rem'
                },
                '@media (max-width: 480px)': {
                  fontSize: '0.85rem'
                },
                lineHeight: 1.6,
                mb: { xs: 3, sm: 3.5, md: 4 },
                maxWidth: { xs: '100%', sm: '100%', md: '100%', lg: '450px' },
                textAlign: { xs: 'center', lg: 'left' }
              }}
            >
              Descubre los secretos de la agricultura orgánica y regenerativa con expertos nacionales e internacionales.
              Aprende técnicas prácticas, económicas y sostenibles, pensadas para profesionales y amantes de la agricultura ecológica.
            </Typography>
            
            <ServiceButton 
              sx={{ 
                mt: { xs: 2, sm: 2.5, md: 3 },
                display: { xs: 'block', lg: 'inline-block' },
                mx: { xs: 'auto', lg: 0 }
              }}
              onClick={() => navigate('/cursos')}
            >
              Más información
            </ServiceButton>
          </Box>

          {/* Images Section */}
          <Box sx={{ 
            display: 'flex',
            gap: { xs: 1.5, sm: 2, md: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            width: { 
              xs: '100%', 
              sm: '100%', 
              md: '100%', 
              lg: '800px', 
              xl: '1100px' 
            },
            ml: { xs: 0, lg: -8, xl: -12.5 },
            mr: { xs: 0, lg: 4, xl: 8 },
            mt: { xs: 4, sm: 5, md: 5, lg: 6 },
            px: { xs: 2, sm: 2, md: 3, lg: 0 },
            // Ultra-wide screens - combined width and ml
            '@media (min-width: 1920px)': {
              width: '1300px',
              ml: -15
            }
          }}>
            {loading ? (
              // Show loading placeholder
              Array.from({ length: 3 }).map((_, index) => (
                <Box key={index} sx={{ flex: 1 }}>
                  <Box sx={{ position: 'relative' }}>
                    <ServiceCard sx={{ 
                      height: { xs: '250px', sm: '280px', md: '320px', lg: '350px', xl: '380px' },
                      '@media (min-width: 1920px)': {
                        height: '420px'
                      },
                      position: 'relative',
                      backgroundColor: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: '#999' }}>Loading...</Typography>
                    </ServiceCard>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#455b40',
                        fontWeight: 700,
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem', lg: '1.2rem' },
                        '@media (min-width: 1920px)': {
                          fontSize: '1.3rem'
                        },
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        mt: { xs: 1.5, sm: 2 },
                        textAlign: 'left'
                      }}
                    >
                      Loading...
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : courses.map((course) => (
              <Box key={course.id} sx={{ flex: 1 }}>
                <Box 
                  sx={{ 
                    position: 'relative',
                    cursor: 'pointer',
                    '&:hover .course-card': {
                      transform: 'translateY(-8px)',
                    }
                  }}
                  onClick={() => navigate(`/cursos/${course.slug}`)}
                >
                  <ServiceCard 
                    className="course-card"
                    sx={{ 
                      height: { xs: '250px', sm: '280px', md: '320px', lg: '350px', xl: '380px' },
                      '@media (min-width: 1920px)': {
                        height: '420px'
                      },
                      position: 'relative',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <ServiceImage
                      sx={{
                        backgroundImage: `url(${course.acf?.image_url_home || '/course.png'})`
                      }}
                    />
                  </ServiceCard>
                  
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#455b40',
                      fontWeight: 700,
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.05rem', lg: '1.1rem' },
                      '@media (min-width: 1920px)': {
                        fontSize: '1.2rem'
                      },
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      mt: { xs: 1.5, sm: 2 },
                      textAlign: 'left'
                    }}
                  >
                    {course.title?.rendered || 'Course Title'}
                  </Typography>
                  
                  <ReadMoreLink onClick={() => navigate(`/cursos/${course.slug}`)}>
                  Más detalles →
                  </ReadMoreLink>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default CourseOverview;